/**
 * Collapsible "Edit customer" form. Self-gates on `customer.write`.
 *
 * Prefilled from the existing row for the plaintext fields (names, refs,
 * language, vulnerable flag). Phone + national ID are *encrypted at rest* and
 * never returned to the browser, so they start blank with a "leave blank to
 * keep current" hint and are only sent when the operator types a new value —
 * the service re-validates them against the country config and re-hashes the
 * lookup index. Omitted fields are left unchanged (PATCH semantics).
 *
 * On success the cached detail row is replaced and the list query invalidated.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Customer, UpdateCustomerRequest } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { languageLabel, useCountryConfig } from "../lib/useCountryConfig.js";

export function EditCustomerForm({
  customer,
}: {
  readonly customer: Customer;
}): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("customer.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const cfg = useCountryConfig(open);
  const [given, setGiven] = useState(customer.givenName);
  const [family, setFamily] = useState(customer.familyName);
  const [other, setOther] = useState(customer.otherNames ?? "");
  const [phone, setPhone] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [photoRef, setPhotoRef] = useState(customer.photoRef);
  const [idDocRef, setIdDocRef] = useState(customer.idDocRef);
  const [language, setLanguage] = useState(customer.language);
  const [vulnerableCustomer, setVulnerableCustomer] = useState(customer.vulnerableCustomer);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateCustomerRequest) =>
      api.patch<Customer>(`/api/customers/${customer.id}`, body),
    onSuccess: (next) => {
      qc.setQueryData<Customer>(["customers", "byId", customer.id], next);
      void qc.invalidateQueries({ queryKey: ["customers", "list"] });
      setPhone("");
      setNationalId("");
      setError(null);
      setOpen(false);
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setGiven(customer.givenName);
    setFamily(customer.familyName);
    setOther(customer.otherNames ?? "");
    setPhone("");
    setNationalId("");
    setPhotoRef(customer.photoRef);
    setIdDocRef(customer.idDocRef);
    setLanguage(customer.language);
    setVulnerableCustomer(customer.vulnerableCustomer);
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (given.trim().length < 1 || family.trim().length < 1) {
      setError("Given and family names are required.");
      return;
    }
    if (photoRef.trim().length < 1 || idDocRef.trim().length < 1) {
      setError("Photo reference and ID document reference are required.");
      return;
    }
    if (language.trim().length < 2) {
      setError("Language must be a 2–8 character code (e.g. sw, en).");
      return;
    }
    const otherTrimmed = other.trim();
    const phoneTrimmed = phone.trim();
    const nationalIdTrimmed = nationalId.trim();
    const body: UpdateCustomerRequest = {
      names: {
        given: given.trim(),
        family: family.trim(),
        ...(otherTrimmed.length > 0 ? { other: otherTrimmed } : {}),
      },
      photoRef: photoRef.trim(),
      idDocRef: idDocRef.trim(),
      language: language.trim(),
      vulnerableCustomer,
      ...(phoneTrimmed.length > 0 ? { phone: phoneTrimmed } : {}),
      ...(nationalIdTrimmed.length > 0 ? { nationalId: nationalIdTrimmed } : {}),
    };
    mutation.mutate(body);
  }

  if (!canWrite) return null;

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Edit customer…
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Edit customer</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Given name">
          <TextInput value={given} onChange={(e) => setGiven(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Family name">
          <TextInput value={family} onChange={(e) => setFamily(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Other names" hint="optional">
          <TextInput value={other} onChange={(e) => setOther(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Language">
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={!cfg.data}
          >
            {(cfg.data?.supportedLanguages ?? [customer.language]).map((c) => (
              <option key={c} value={c}>
                {languageLabel(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Phone" hint="leave blank to keep current">
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="National ID" hint="leave blank to keep current">
          <TextInput value={nationalId} onChange={(e) => setNationalId(e.target.value)} />
        </Field>
        <Field label="Photo reference" hint="object-store ref">
          <TextInput value={photoRef} onChange={(e) => setPhotoRef(e.target.value)} />
        </Field>
        <Field label="ID document reference" hint="object-store ref">
          <TextInput value={idDocRef} onChange={(e) => setIdDocRef(e.target.value)} />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
        <input
          type="checkbox"
          checked={vulnerableCustomer}
          onChange={(e) => setVulnerableCustomer(e.target.checked)}
          className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        Vulnerable customer
      </label>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Save changes"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
